import { TellescopeModelFromName, useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import { htmlToText } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { pendo } from "../../../../../tracking";
import { renderEmailForForwardedMessage } from "@tellescope/utils";
import {
  MessageCompositionFooter,
  MessageCompositionFooterPayload,
} from "../../../../../components/MessageCompositionFooter";
import { buildHtmlMessageWithFiles } from "../../../utils";
import { ForwardedEmailPreview } from "../ForwardedEmailPreview";

export interface InboxEmailCompositionFooterProps {
  enduserId?: string;
  selectedEmailAddress?: string;
  ccEmailAddresses?: string[];
  subject?: string;
  onSend?: (email: TellescopeModelFromName<"emails">) => void;
  emailIdToForward?: string;
}

export const InboxEmailCompositionFooter = ({
  enduserId,
  selectedEmailAddress,
  ccEmailAddresses,
  subject,
  onSend,
  emailIdToForward,
}: InboxEmailCompositionFooterProps) => {
  const { t } = useTranslation("inbox");

  const emailToForwardQuery = useTellescopeQuery({
    model: "emails",
    method: "getOne",
    request: emailIdToForward ?? "",
    enabled: !!emailIdToForward,
  });

  const sendEmailMutation = useTellescopeMutation({
    model: "emails",
    method: "createOne",
    onSuccess: (email) => onSend?.(email),
    onError: (error) => console.error("Failed to send email:", error),
  });

  const canSend = !!enduserId && !!subject && !sendEmailMutation.isPending;

  const handleSend = ({ message, files }: MessageCompositionFooterPayload, sendAt?: Date) => {
    if (!enduserId || !subject) return;
    const resolvedContent =
      buildHtmlMessageWithFiles(message, files) +
      (emailToForwardQuery.data ? renderEmailForForwardedMessage(emailToForwardQuery.data) : "");
    sendEmailMutation.mutate({
      enduserId,
      alternateToAddress: selectedEmailAddress,
      cc: (ccEmailAddresses?.length ?? 0) > 0 ? ccEmailAddresses : undefined,
      isMarketing: false,
      HTMLContent: resolvedContent,
      textContent: htmlToText(resolvedContent),
      subject,
      ...(sendAt && { sendAt }),
      inbound: false,
    });
  };

  return (
    <MessageCompositionFooter
      editorVariant="richtext"
      channelType="email"
      placeholder={t("Write your message...")}
      canSend={canSend}
      enduserId={enduserId}
      leadingActions={emailToForwardQuery.data && <ForwardedEmailPreview email={emailToForwardQuery.data} />}
      onSubmit={(payload) => {
        pendo.track("new-email-thread-send");
        handleSend(payload);
      }}
      onSchedule={(payload, sendAt) => handleSend(payload, sendAt)}
      trackingIds={{ schedule: "email-compose-schedule-message-dialog-open" }}
    />
  );
};
